import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Upload } from "lucide-react";
import { KINDS, RELIABILITY } from "@/components/HistoryEntry";

const blank = (section_key) => ({
  section_key, kind: "text", title: "", body: "", reliability: "hypothesis", date_label: "", year: "",
  year_is_estimate: true, reference: "", witness_name: "", file_path: null, visibility: "members", order: 0,
  pages: "", divergences: "", validation_status: "to_verify", related_section_keys: [], end_year: "",
});

const Sel = ({ value, onChange, options, testid }) => (
  <select value={value} onChange={e => onChange(e.target.value)} className="w-full h-9 rounded-md border border-input bg-transparent px-3 text-sm" data-testid={testid}>
    {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);

export const HistoryEntryDialog = ({ open, onOpenChange, entry, sectionKey, sections, onSaved, proposal = false }) => {
  const { API } = useAuth();
  const [f, setF] = useState(blank(sectionKey));
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  useEffect(() => { if (open) setF(entry ? { ...blank(sectionKey), ...entry, year: entry.year ?? "" } : blank(sectionKey)); }, [open, entry, sectionKey]);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const upload = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await axios.post(`${API}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("file_path", data.path); toast.success("Fichier ajouté");
    } catch { toast.error("Échec du téléversement"); } finally { setUploading(false); }
  };

  const save = async () => {
    if (!f.title.trim()) { toast.error("Le titre est obligatoire"); return; }
    setBusy(true);
    try {
      const num = v => (v === "" || v == null ? null : Number(v));
      const payload = { ...f, year: num(f.year), end_year: num(f.end_year), order: Number(f.order) || 0 };
      ["id", "archived", "created_at", "updated_at", "created_by_name", "updated_by_name", "pending", "proposed_by", "approved_by_name", "approved_at"].forEach(k => delete payload[k]);
      if (proposal) await axios.post(`${API}/history/proposals`, payload);
      else if (entry?.id) await axios.put(`${API}/history/entries/${entry.id}`, payload);
      else await axios.post(`${API}/history/entries`, payload);
      toast.success(proposal ? "Proposition envoyée — elle sera examinée par un administrateur" : "Contenu enregistré"); onOpenChange(false); onSaved?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#FAF7F2] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="history-entry-dialog">
        <DialogHeader><DialogTitle className="font-serif-heritage text-2xl text-[#261911]">{entry ? "Modifier le contenu" : "Ajouter un contenu"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label>Rubrique</Label><Sel value={f.section_key} onChange={v => set("section_key", v)} options={sections.map(s => [s.key, s.parent_key ? `— ${s.title}` : s.title])} testid="entry-section" /></div>
          <div><Label>Type</Label><Sel value={f.kind} onChange={v => set("kind", v)} options={Object.entries(KINDS).map(([k, v]) => [k, v.label])} testid="entry-kind" /></div>
          <div className="sm:col-span-2"><Label>Titre *</Label><Input value={f.title} onChange={e => set("title", e.target.value)} data-testid="entry-title" /></div>
          <div><Label>Fiabilité</Label><Sel value={f.reliability} onChange={v => set("reliability", v)} options={Object.entries(RELIABILITY).map(([k, v]) => [k, v.label])} testid="entry-reliability" /></div>
          {!proposal && <div><Label>Visibilité</Label><Sel value={f.visibility} onChange={v => set("visibility", v)} options={[["members", "Membres validés uniquement"], ["public", "Public"]]} testid="entry-visibility" /></div>}
          <div><Label>Année (pour la frise)</Label><Input type="number" value={f.year} onChange={e => set("year", e.target.value)} data-testid="entry-year" />
            <label className="flex items-center gap-2 text-xs text-[#6E5B4F] mt-1"><input type="checkbox" checked={!!f.year_is_estimate} onChange={e => set("year_is_estimate", e.target.checked)} data-testid="entry-year-estimate" /> Date estimative</label></div>
          <div><Label>Année de fin (période)</Label><Input type="number" value={f.end_year ?? ""} onChange={e => set("end_year", e.target.value)} data-testid="entry-end-year" /></div>
          {!proposal && <div><Label>Statut de validation</Label><Sel value={f.validation_status} onChange={v => set("validation_status", v)} options={[["to_verify", "À vérifier"], ["testimony_confirmed", "Confirmée par témoignage"], ["cross_validated", "Validée après recoupement"]]} testid="entry-validation" /></div>}
          <div><Label>Pages citées</Label><Input value={f.pages} placeholder="ex. 99–113" onChange={e => set("pages", e.target.value)} data-testid="entry-pages" /></div>
          <div><Label>Date en toutes lettres (facultatif)</Label><Input value={f.date_label} placeholder="ex. fin du XIXe siècle" onChange={e => set("date_label", e.target.value)} data-testid="entry-date-label" /></div>
        </div>
        <div><Label>Texte</Label><Textarea rows={7} value={f.body} onChange={e => set("body", e.target.value)} data-testid="entry-body" /></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label>Témoin (pour un témoignage)</Label><Input value={f.witness_name} onChange={e => set("witness_name", e.target.value)} data-testid="entry-witness" /></div>
          <div><Label>Ordre d'affichage</Label><Input type="number" value={f.order} onChange={e => set("order", e.target.value)} data-testid="entry-order" /></div>
        </div>
        <div><Label>Divergences entre témoignages ou sources</Label><Textarea rows={3} value={f.divergences} onChange={e => set("divergences", e.target.value)} data-testid="entry-divergences-input" /></div>
        <div><Label>Rubriques liées</Label>
          <div className="flex flex-wrap gap-2 mt-1">{sections.filter(s => s.key !== f.section_key).map(s => (
            <label key={s.key} className="text-xs flex items-center gap-1"><input type="checkbox" checked={(f.related_section_keys || []).includes(s.key)}
              onChange={e => set("related_section_keys", e.target.checked ? [...(f.related_section_keys || []), s.key] : f.related_section_keys.filter(k => k !== s.key))} />{s.title}</label>
          ))}</div>
        </div>
        <div><Label>Référence / source (auteur, titre, éditeur, année, page)</Label><Textarea rows={2} value={f.reference} onChange={e => set("reference", e.target.value)} data-testid="entry-reference-input" /></div>
        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-2 text-sm text-[#993D22] cursor-pointer" data-testid="entry-upload">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Joindre une photo ou un document
            <input type="file" className="hidden" onChange={upload} />
          </label>
          {f.file_path && <span className="text-xs text-[#2C4230]">Fichier joint ✓ <button className="underline ml-1" onClick={() => set("file_path", null)}>retirer</button></span>}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button className="btn-terra" onClick={save} disabled={busy} data-testid="save-entry-btn">{busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Enregistrer</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default HistoryEntryDialog;
